import { Injectable } from '@angular/core';
import { Assistant, assistants } from '../../types/assistant';
import { COLOR_SCHEMES, DEFAULT_COLOR } from '../../types/color-scheme';
import { Message } from '../../types/message';
import { BehaviorSubject, firstValueFrom, catchError, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '~/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AssistantService {
  private assistants = new BehaviorSubject<Assistant[]>([]);
  private readonly _API_URL = environment.apiUrl;

  assistants$ = this.assistants.asObservable();

  constructor(private _httpClient: HttpClient) {
    this.loadAssistants();
  }

  async loadAssistants() {
    // TODO: replace this with API call
    this.assistants.next(assistants);
  }

  getAssistantById(assistantId: string) {
    return this.assistants.value.find(({ id }) => id === assistantId);
  }

  getAssistantColors(assistantId: string) {
    const assistant = this.getAssistantById(assistantId);
    return COLOR_SCHEMES[assistant?.color || DEFAULT_COLOR];
  }

  async getAssistantHistory(insightType: string, businessId: string, limit: number): Promise<Message[]> {
    // Default welcome messages when no insight or business is selected
    if (!insightType || !businessId) {
      return [
        {
          role: 'bot' as const,
          message: "Hello! I'm your personal business assistant.",
          timestamp: new Date(Date.now() - 1000) // Slightly older message
        },
        {
          role: 'bot' as const,
          message: "Once you pick an area of your business to focus on we can get started.",
          timestamp: new Date() // Current message
        }
      ];
    }

    const url = `${this._API_URL}/api/v1/assistant/history?insightType=${insightType}&businessId=${businessId}&limit=${limit}`;
    try {
      const response = await firstValueFrom(
        this._httpClient.get<any[]>(url, { withCredentials: true })
          .pipe(
            catchError(error => {
              console.error('Error fetching assistant history:', error);
              // Return empty array instead of throwing error
              return [];
            })
          )
      );

      // If response is not an array or empty, return default messages
      if (!response || !Array.isArray(response) || response.length === 0) {
        console.log('Empty or invalid response received from history API');
        return [
          {
            role: 'bot' as const,
            message: "Let's start a new conversation about your business.",
            timestamp: new Date()
          }
        ];
      }

      // Messages should already be sorted on the server in chronological order
      // If not, we can ensure they are sorted here
      
      const messages: Message[] = response.map((msg: any) => {
        if (!msg) return {
          role: 'bot' as const,
          message: "Message data is unavailable.",
          timestamp: new Date()
        };

        // Extract the text content from the message
        let messageText = '';
        
        // Safely extract content
        if (msg.content && Array.isArray(msg.content)) {
          messageText = msg.content
            .filter((content: any) => content && content.text && content.text.value)
            .map((content: any) => content.text.value)
            .join(' ');
        } else if (typeof msg.message === 'string') {
          // If the message is already in our format
          messageText = msg.message;
        }
        
        let choices: string[] | undefined = undefined;
        
        // Try to parse message as JSON to extract message and choices
        try {
          const parsed = JSON.parse(messageText);
          if (parsed && typeof parsed.message === 'string') {
            messageText = parsed.message;
          }
          if (parsed && Array.isArray(parsed.choices)) {
            choices = parsed.choices;
          }
        } catch {
          // Not valid JSON, use the string as is
        }
        
        // Create a proper timestamp - ensure it's actually a valid timestamp
        let timestamp: Date;
        if (msg.timestamp) {
          // If we already have a timestamp from our API
          timestamp = new Date(msg.timestamp);
        } else if (msg.created_at) {
          // If we have created_at from OpenAI (seconds timestamp)
          timestamp = new Date(Number(msg.created_at) * 1000);
        } else {
          // Fallback with staggered timestamps to ensure chronological display
          const offset = response.indexOf(msg) * 1000; // 1 second between messages
          timestamp = new Date(Date.now() - (response.length * 1000) + offset);
        }
        
        // Validate timestamp
        if (isNaN(timestamp.getTime())) {
          timestamp = new Date(); // Fallback
        }
        
        // Ensure role is strictly typed as 'user' or 'bot'
        const role: 'user' | 'bot' = msg.role === 'user' ? 'user' : 'bot';
        
        return {
          role,
          message: messageText || 'No message content available',
          choices,
          timestamp
        };
      });
      
      // Ensure messages are sorted by timestamp (oldest to newest)
      return messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    } catch (error) {
      console.error('Error fetching assistant history:', error);
      // Return a friendly message instead of empty array
      return [{
        role: 'bot' as const,
        message: "I'm having trouble retrieving our conversation history. Let's continue from here.",
        timestamp: new Date()
      }];
    }
  }

  async getLastUsedAssistant() {
    // TODO: replace this with API call
    const randomNumber = Math.floor(Math.random() * this.assistants.value.length);
    return this.assistants.value[randomNumber];
  }
}
